import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';

const errorText = (e) => e.response?.data?.error || e.message;
const formatDate = (s) => s ? new Date(s).toLocaleString('en-ZA') : '—';

const bytes = (n) => {
  if (n == null) return '—';
  if (n >= 1e9) return `${(n / 1e9).toFixed(1)} GB`;
  if (n >= 1e6) return `${Math.round(n / 1e6)} MB`;
  return `${Math.round(n / 1e3)} KB`;
};

const uptime = (s) => {
  if (s == null) return '—';
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  return days > 0 ? `${days} days ${hours} h` : `${hours} h ${Math.floor((s % 3600) / 60)} min`;
};

// "ghcr.io/antonswarts15/eduthrift-backend:9a40103…" → { repo: 'eduthrift-backend', tag: '9a40103' }
const imageName = (image) => {
  const tags = image.tags || [];
  if (tags.length === 0) return { repo: 'untagged', labels: [] };
  const repo = tags[0].slice(0, tags[0].lastIndexOf(':')).split('/').pop();
  const labels = tags.map(t => {
    const tag = t.slice(t.lastIndexOf(':') + 1);
    return /^[0-9a-f]{40}$/.test(tag) ? tag.slice(0, 7) : tag;
  });
  return { repo, labels };
};

const FOLDER_LABELS = {
  podmanStorage: 'App images & containers',
  uploads: 'Uploaded photos & documents',
  backups: 'Database backups',
  journal: 'System logs',
};

const ServerPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [removing, setRemoving] = useState(false);
  const [message, setMessage] = useState(null);

  const load = useCallback(async () => {
    try {
      setError('');
      const res = await api.get('/admin/server');
      setData(res.data);
    } catch (e) {
      setError(errorText(e) || 'Failed to load server stats.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, [load]);

  const stats = data?.stats;

  // Newest unused image of each app is kept back as the rollback, so "Select old images" skips it
  const images = useMemo(() => {
    const seenRollback = new Set();
    return (stats?.images || []).map(image => {
      const { repo, labels } = imageName(image);
      let status = 'old';
      if (image.inUse) status = 'inUse';
      else if (repo !== 'untagged' && !seenRollback.has(repo)) {
        seenRollback.add(repo);
        status = 'rollback';
      }
      return { ...image, repo, labels, status };
    });
  }, [stats]);

  const selectedImages = images.filter(i => selected.has(i.id));

  const toggle = (id) => setSelected(s => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const selectOld = () => setSelected(new Set(images.filter(i => i.status === 'old').map(i => i.id)));

  const pollResult = async (requestId) => {
    for (let i = 0; i < 60; i++) {
      await new Promise(r => setTimeout(r, 3000));
      const res = await api.get(`/admin/server/remove-images/${requestId}`);
      if (res.data.done) return res.data;
    }
    throw new Error("The server helper hasn't picked up the request. Check its cron job on the server.");
  };

  const removeSelected = async () => {
    const rollbacks = selectedImages.filter(i => i.status === 'rollback').length;
    const ok = window.confirm(`Remove ${selectedImages.length} image${selectedImages.length === 1 ? '' : 's'} from the server?`
      + (rollbacks ? `\n\n${rollbacks} of them ${rollbacks === 1 ? 'is a rollback image' : 'are rollback images'} — after removing, rolling back means pulling the image from GitHub again.` : '')
      + '\n\nImages a running app uses are never removed.');
    if (!ok) return;
    setRemoving(true);
    setMessage(null);
    try {
      const res = await api.post('/admin/server/remove-images', { imageIds: selectedImages.map(i => i.id) });
      const result = await pollResult(res.data.requestId);
      const failed = result.failed || [];
      setMessage({
        ok: failed.length === 0 && !result.error,
        text: result.error ? result.error
          : `Removed ${result.removed.length} image${result.removed.length === 1 ? '' : 's'}, ${bytes(result.freedBytes)} freed.`
            + (failed.length ? ` ${failed.length} not removed: ${failed.map(f => `${f.id} (${f.error})`).join('; ')}` : ''),
      });
      setSelected(new Set());
      await load();
    } catch (e) {
      setMessage({ ok: false, text: errorText(e) });
    } finally {
      setRemoving(false);
    }
  };

  if (loading) return <div style={{ padding: '24px' }}>Loading server stats…</div>;

  const disk = data?.disk;
  const usedPercent = disk?.totalBytes ? Math.round(100 * (disk.totalBytes - disk.freeBytes) / disk.totalBytes) : null;
  const diskColour = usedPercent >= data?.criticalPercent ? '#e74c3c' : usedPercent >= data?.warnPercent ? '#f39c12' : '#27ae60';
  const memory = stats?.memory;
  const memoryPercent = memory?.totalBytes ? Math.round(100 * (memory.totalBytes - memory.availableBytes) / memory.totalBytes) : null;

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', gap: '16px' }}>
        <div>
          <h2 style={{ margin: 0 }}>Server</h2>
          <p style={{ margin: '4px 0 0', color: '#7f8c8d', fontSize: '13px', maxWidth: '760px' }}>
            Every deploy downloads new app images and keeps the old ones, which fills the disk.
            Remove old images here. You get an email when the disk is {data?.warnPercent}% full,
            and another at {data?.criticalPercent}%.
          </p>
        </div>
        <button onClick={load} style={{ padding: '8px 16px', cursor: 'pointer' }}>Refresh</button>
      </div>

      {error && <p style={{ color: 'red' }}>{error}</p>}

      {data?.statsStale && (
        <div style={{ ...notice, backgroundColor: '#fdecea', borderColor: '#e74c3c' }}>
          {stats
            ? `These figures are ${Math.round(data.statsAgeSeconds / 60)} minutes old: the server-stats helper (cron job) has stopped. Removing images won't work until it runs again.`
            : "The server-stats helper isn't running on the server yet, so only the disk figure is available."}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        <div style={card}>
          <div style={cardLabel}>Disk</div>
          <div style={{ ...cardValue, color: diskColour }}>{usedPercent != null ? `${usedPercent}% full` : '—'}</div>
          <div style={bar}><div style={{ ...barFill, width: `${usedPercent || 0}%`, backgroundColor: diskColour }} /></div>
          <div style={muted}>{bytes(disk?.freeBytes)} free of {bytes(disk?.totalBytes)}</div>
        </div>
        <div style={card}>
          <div style={cardLabel}>Memory</div>
          <div style={cardValue}>{memoryPercent != null ? `${memoryPercent}% used` : '—'}</div>
          <div style={bar}><div style={{ ...barFill, width: `${memoryPercent || 0}%`, backgroundColor: '#3498db' }} /></div>
          <div style={muted}>{bytes(memory?.availableBytes)} available of {bytes(memory?.totalBytes)}</div>
        </div>
        <div style={card}>
          <div style={cardLabel}>CPU load (1 / 5 / 15 min)</div>
          <div style={cardValue}>{stats?.load ? stats.load.map(l => l.toFixed(2)).join(' / ') : '—'}</div>
          <div style={muted}>{stats?.cpus ? `${stats.cpus} CPUs — busy when above ${stats.cpus}` : ''}</div>
        </div>
        <div style={card}>
          <div style={cardLabel}>Up for</div>
          <div style={cardValue}>{uptime(stats?.uptimeSeconds)}</div>
          <div style={muted}>Updated {formatDate(stats?.generatedAt)}</div>
        </div>
      </div>

      {stats && (
        <>
          <h3 style={h3}>What uses the space</h3>
          <table style={{ ...table, maxWidth: '560px', marginBottom: '24px' }}>
            <tbody>
              {Object.entries(FOLDER_LABELS).map(([key, label]) => (
                <tr key={key} style={row}>
                  <td style={td}>{label}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{bytes(stats.folders?.[key])}</td>
                </tr>
              ))}
              <tr style={row}>
                <td style={td}>Of which unused app images (can be removed)</td>
                <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{bytes(stats.imagesDisk?.reclaimableBytes)}</td>
              </tr>
            </tbody>
          </table>

          <h3 style={h3}>Running apps</h3>
          <table style={{ ...table, marginBottom: '24px' }}>
            <thead>
              <tr style={{ backgroundColor: '#f5f5f5' }}>
                <th style={th}>Container</th><th style={th}>Image</th><th style={th}>Status</th>
              </tr>
            </thead>
            <tbody>
              {(stats.containers || []).map(c => (
                <tr key={c.name} style={row}>
                  <td style={td}>{c.name}</td>
                  <td style={td}><span style={muted}>{c.imageId}</span></td>
                  <td style={{ ...td, color: c.state === 'running' ? '#27ae60' : '#e74c3c' }}>{c.status}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '8px' }}>
            <h3 style={{ ...h3, margin: 0 }}>App images ({images.length})</h3>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button onClick={selectOld} disabled={removing} style={{ ...btn, backgroundColor: '#ecf0f1' }}>Select old images</button>
              {selected.size > 0 && (
                <button onClick={() => setSelected(new Set())} disabled={removing} style={{ ...btn, backgroundColor: '#ecf0f1' }}>Clear</button>
              )}
              <button onClick={removeSelected} disabled={removing || selected.size === 0 || data?.statsStale}
                style={{ ...btn, backgroundColor: selected.size === 0 ? '#bdc3c7' : '#e74c3c', color: '#fff' }}>
                {removing ? 'Removing…' : `Remove selected (${selected.size})`}
              </button>
            </div>
          </div>
          <p style={{ ...muted, margin: '0 0 12px' }}>
            Old images share parts with each other, so removing them frees less than their sizes add up to.
            "Select old images" keeps the newest unused image of each app so a deploy can still be rolled back.
          </p>

          {message && (
            <div style={{ ...notice, backgroundColor: message.ok ? '#e8f5e9' : '#fdecea', borderColor: message.ok ? '#27ae60' : '#e74c3c' }}>
              {message.ok ? '✅ ' : '❌ '}{message.text}
            </div>
          )}

          <table style={table}>
            <thead>
              <tr style={{ backgroundColor: '#f5f5f5' }}>
                <th style={th}></th><th style={th}>App</th><th style={th}>Version</th><th style={th}>Built</th>
                <th style={th}>Size</th><th style={th}>Status</th>
              </tr>
            </thead>
            <tbody>
              {images.map(i => (
                <tr key={i.id} style={{ ...row, backgroundColor: selected.has(i.id) ? '#fff5f5' : undefined }}>
                  <td style={td}>
                    <input type="checkbox" checked={selected.has(i.id)} disabled={i.inUse || removing} onChange={() => toggle(i.id)} />
                  </td>
                  <td style={td}>{i.repo}</td>
                  <td style={td}>{i.labels.join(', ') || <span style={muted}>{i.id}</span>}</td>
                  <td style={td}>{formatDate(i.created)}</td>
                  <td style={td}>{bytes(i.sizeBytes)}</td>
                  <td style={td}>
                    {i.status === 'inUse' ? <span style={{ color: '#27ae60', fontWeight: 600 }}>In use</span>
                      : i.status === 'rollback' ? <span style={{ color: '#2980b9' }}>Rollback</span>
                      : <span style={muted}>Old</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
};

const card = { backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '16px' };
const cardLabel = { fontSize: '13px', color: '#7f8c8d', marginBottom: '6px' };
const cardValue = { fontSize: '22px', fontWeight: 600, marginBottom: '8px' };
const bar = { height: '8px', backgroundColor: '#ecf0f1', borderRadius: '4px', overflow: 'hidden', marginBottom: '8px' };
const barFill = { height: '100%', borderRadius: '4px' };
const notice = { border: '1px solid', borderRadius: '6px', padding: '10px 14px', fontSize: '13px', marginBottom: '16px' };
const h3 = { fontSize: '16px', margin: '0 0 10px' };
const table = { width: '100%', borderCollapse: 'collapse', fontSize: '14px' };
const row = { borderBottom: '1px solid #eee' };
const th = { padding: '10px 12px', textAlign: 'left', fontWeight: '600', borderBottom: '2px solid #ddd' };
const td = { padding: '8px 12px', verticalAlign: 'top' };
const muted = { color: '#888', fontSize: '12px' };
const btn = { padding: '6px 12px', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' };

export default ServerPage;
